import { useCallback, useEffect, useMemo, useRef } from "react";
import type { ModalRef } from "../../components/Modal";
import { useSearchParams } from "../../hooks";
import { useTranslation } from "react-i18next";
import { APIs } from "../../apis";
import { useApi } from "../../hooks";
import { Constants } from "../../utils";
import { Tags } from "../../libs";
import { TagsEvent, TagsContentType } from "../../libs/tags";

export type PaymentSuccessViewModel = {
  modalRef: React.RefObject<ModalRef | null>;
  printTargetRef: React.RefObject<HTMLDivElement | null>;
  code: string;
  checklistKeys: ReadonlyArray<string>;
  t: (key: string, options?: Record<string, unknown>) => string;
  printConfirmation: () => void;
  close: () => void;
  goToBooking: () => void;
  startNewBooking: () => void;
};

async function tagBookingSuccess(orderId: string) {
  try {
    const { order } = await APIs.order.getOrder(orderId);
    const item = order.order_items?.[0] as any;

    Tags.event(TagsEvent.Purchase, {
      ecommerce: Tags.buildEcommerce(
        (order.order_items ?? []).map((it) => ({
          item_id: it.id,
          item_name: it.productName,
          price: it.payableAmount ?? undefined,
          affiliation: order.partner?.name,
          quantity: 1,
        })),
        {
          transaction_id: String(order.id ?? orderId),
          value: order.payableAmount ?? order.paidAmount ?? undefined,
        },
      ),
    });
    Tags.event(TagsEvent.BookingCompleted, {
      booking_id: String(order.code ?? order.id ?? orderId),
      country:
        (typeof order.countryCode === "string" && order.countryCode) ||
        item?.product?.countryCode ||
        item?.partnerLocation?.countryCode ||
        item?.partnerLocation?.city?.countryCode,
      city: item?.partnerLocation?.city?.name,
      service:
        item?.product?.partner_service?.service?.name || item?.productName,
      language:
        item?.product?.language || item?.product?.partner_service?.language,
    });
  } catch {
    Tags.event(TagsEvent.Purchase, {
      ecommerce: Tags.buildEcommerce([], { transaction_id: orderId }),
    });
    Tags.event(TagsEvent.BookingCompleted, { booking_id: orderId });
  }
}

export default function useViewModel(): PaymentSuccessViewModel {
  const { t } = useTranslation();
  const modalRef = useRef<ModalRef>(null);
  const printTargetRef = useRef<HTMLDivElement>(null);
  const { fetch } = useApi();
  const { searchParams, setSearchParams } = useSearchParams({
    paymentSuccess: false,
    order_id: "",
    code: "",
    telehealth_order: false,
    free: false,
  });

  const close = useCallback(() => {
    setSearchParams({
      paymentSuccess: null,
      order_id: null,
      code: null,
      telehealth_order: null,
      free: null,
    });
    modalRef.current?.close();
  }, [setSearchParams]);

  const getData = useCallback(async () => {
    if (
      !searchParams.paymentSuccess ||
      !searchParams.order_id ||
      !searchParams.code
    ) {
      close();
      return;
    }
    setSearchParams({
      payment_intent: null,
      payment_intent_client_secret: null,
      redirect_status: null,
    });
    const orderId = searchParams.order_id;
    const purchaseKey = `purchase_tracked_${orderId}`;
    if (orderId && !sessionStorage.getItem(purchaseKey)) {
      sessionStorage.setItem(purchaseKey, "1");
      void tagBookingSuccess(orderId);
    }
    modalRef.current?.open();
  }, [
    close,
    setSearchParams,
    searchParams.order_id,
    searchParams.paymentSuccess,
    searchParams.code,
  ]);

  useEffect(() => {
    void getData();
    const modal = modalRef.current;
    return () => {
      modal?.close();
    };
  }, [getData]);

  const checklistKeys = useMemo(
    () =>
      [
        "paymentSuccess.invoiceGenerated",
        "paymentSuccess.clinicNotifiedOfPatientArrival",
        "paymentSuccess.patientNotifiedOfBookingAndLocation",
      ] as const,
    [],
  );

  const printConfirmation = useCallback(async () => {
    try {
      if (!searchParams.order_id) return;
      const res = await fetch(
        () =>
          APIs.order.printSuccessOrderPdf(searchParams.order_id, {
            telehealth_order: !!searchParams.telehealth_order,
            free: !!searchParams.free,
          }),
        {
          successMsg: (r) =>
            t("paymentSuccess.downloadExpires", {
              seconds: r.expires_in_seconds,
            }),
          showErrorAlert: true,
        },
      );

      if (!res?.download_url) return;
      Tags.event(TagsEvent.PrintConfirmation);
      window.open(res.download_url, "_blank", "noopener,noreferrer");
    } catch {}
  }, [
    fetch,
    searchParams.free,
    searchParams.order_id,
    searchParams.telehealth_order,
    t,
  ]);

  const goToBooking = useCallback(() => {
    if (!searchParams.order_id) return;
    Tags.event(TagsEvent.SelectContent, {
      content_type: TagsContentType.BookingDetail,
      item_id: String(searchParams.order_id),
    });
    window.open(
      `${Constants.RoutePath.ManageBookings}/${searchParams.order_id}`,
      "_blank",
      "noopener,noreferrer",
    );
  }, [searchParams.order_id]);

  const startNewBooking = useCallback(() => {
    Tags.event(TagsEvent.SelectContent, {
      content_type: TagsContentType.StartNewBooking,
    });
    close();
  }, [close]);

  return {
    t,
    modalRef,
    printTargetRef,
    code: searchParams.code ?? "-",
    checklistKeys,
    close,
    printConfirmation,
    goToBooking,
    startNewBooking,
  };
}
